/**
 * Copyright IBM Corp. 2021
 *
 * This source code is licensed under the IBM Public License Version 1.0 found in the
 * LICENSE file in the root directory of this source tree.
 */
import memo from "memoize-one";
import isEmpty from "lodash/isEmpty";
import debounce from "lodash/debounce";
import TriFloorPlanPlugin from "./TriFloorPlanPlugin.js";
import TriFloorPlanUtils from "../util/TriFloorPlanUtils.js";
import TriNavPlugin from "./TriNavPlugin.js"
import TriPanPlugin from "./TriPanPlugin.js"
import TriZoomPlugin from "./TriZoomPlugin.js"

export default class TriSelectablePlugin extends TriFloorPlanPlugin {
    static type = "selectable";

    constructor()
    {
        super();

        this.dragSelect = false;
        this.handleDragSelectEnd = this.handleDragSelectEnd.bind( this );
        this.zoomToFit = false;
    }

    get selectableClassName()
    {
        return this.props.selectableClassName != null
          ? this.props.selectableClassName
          : "selectable";
    }

    get selectedClassName() {
        return this.props.selectedClassName != null
          ? this.props.selectedClassName
          : "selected";
    }

    get selected() {
        if (this.props.selected != null) {
            return this.props.selected;
        }
        return this.props.multi ? [] : null;
    }

    memoizedSelectableById = memo((selectable) =>
        this.computeSelectableById(selectable)
    );

    get selectableById() {
        return this.memoizedSelectableById(this.props.selectable);
    }

    pluginDidUpdate() {
        const { floorPlanLoaded, enabled } = this.props;
        if (!floorPlanLoaded || !enabled) {
            this.stop();
            return;
        }
        this.start();
        if( this.dragSelect !== this.props.dragSelect )
        {
            this.dragSelect = this.props.dragSelect;
            if( this.dragSelect && this.props.multi )
            {
                this.addDragSelectListeners();
            }
            else
            {
                this.removeDragSelectListeners();
            }
        }
        if( this.zoomToFit != this.props.zoomToSelection )
        {
            this.zoomToFit = this.props.zoomToSelection;
            if( this.zoomToFit ) this.zoomToSelection( this.selected );
        }
    }

    pluginDidRemove() {
        this.stop();
    }

    start() {
        this.addSvgTapListener();

        if(    this.props.selectable !== this.lastSelectable
            || this.lastSelectableClassName !== this.selectableClassName )
        {
            this.updateSelectable(this.selectableById);
            this.lastSelectable = this.props.selectable;
            this.lastSelectableClassName = this.selectableClassName;
        }
        if(    this.props.selected !== this.lastSelected
            || this.lastSelectedClassName !== this.selectedClassName )
        {
            this.updateSelected(this.selected);
            this.lastSelectedClassName = this.selectedClassName
            this.lastSelected = this.props.selected;
            if( this.props.zoomToSelection ) this.zoomToSelection( this.selected );
        }
    }

    stop() {
      this.lastSelectable = [];
        const { triFloorPlan, floorPlanLoaded } = this.props;
        if (!floorPlanLoaded) return;

        this.removeSvgTapListener();
        if( this.dragSelect ) this.removeDragSelectListeners();

        TriFloorPlanUtils.clearAllClasses(
            this.selectableClassName,
            triFloorPlan.svgElement
        );
        TriFloorPlanUtils.clearAllClasses(
            this.selectedClassName,
            triFloorPlan.svgElement
        );
    }

    updateSelectable( selectableById )
    {
        const { floorPlanLoaded, enabled, triFloorPlan } = this.props;
        if (!floorPlanLoaded || !enabled) return;

        TriFloorPlanUtils.clearAllClasses(
            this.lastSelectableClassName,
            triFloorPlan.svgElement
        );

        if( !selectableById || selectableById.size === 0 ) return;

        selectableById.forEach((value, key) => {
            TriFloorPlanUtils.addClass(
                key,
                this.selectableClassName,
                triFloorPlan.attachedMap
            );
            TriFloorPlanUtils.addClass(
                key,
                this.selectableClassName,
                triFloorPlan.interactiveMap
            );
        });
    }

    updateSelected(selected) {
        const { floorPlanLoaded, enabled, triFloorPlan, multi } = this.props;
        if (!enabled || !floorPlanLoaded) return;

        TriFloorPlanUtils.clearAllClasses(
            this.lastSelectedClassName,
            triFloorPlan.svgElement
        );

        this.disableTap = false;

        if( isEmpty( selected ) )
        {
            return;
        }
        const newSelected = multi ? selected : [selected];
        for (const item of newSelected) {
            TriFloorPlanUtils.addClass(
                TriFloorPlanUtils.getPropValue(item, TriSelectablePlugin.SPACE_ID),
                this.selectedClassName,
                triFloorPlan.attachedMap
            );
            TriFloorPlanUtils.addClass(
                TriFloorPlanUtils.getPropValue(item, TriSelectablePlugin.SPACE_ID),
                this.selectedClassName,
                triFloorPlan.interactiveMap
            );
        }
    }

    zoomToSelection( selected )
    {
        const { floorPlanLoaded, enabled, triFloorPlan, multi } = this.props;
        const { svgElement, svgViewPort } = triFloorPlan;
        const container = svgElement.parentElement;
        if( !enabled || !floorPlanLoaded ) return;

        let zoomPlugin;
        triFloorPlan.pluginInstances.forEach( ( instance ) =>
        {
            if( instance.props.type === TriZoomPlugin.type ) zoomPlugin = instance;
        } );
        if( !zoomPlugin ) return;

        if( isEmpty( selected ) )
        {
            return;
        }

        let minX, minY, maxX, maxY;
        const newSelected = multi ? selected : [ selected ];
        for( const item of newSelected )
        {
            const spaceId = TriFloorPlanUtils.getPropValue( item, TriSelectablePlugin.SPACE_ID );
            const element = triFloorPlan.attachedMap.get( spaceId );
            if( !element ) continue;
            const bbox = element.getBBox();
            if( !minX )
            {
                minX = bbox.x;
                minY = bbox.y;
                maxX = bbox.x + bbox.width;
                maxY = bbox.y + bbox.height;
            }
            else
            {
                if( bbox.x < minX ) minX = bbox.x;
                if( bbox.y < minY ) minY = bbox.y;
                if( bbox.x + bbox.width > maxX ) maxX = bbox.x + bbox.width;
                if( bbox.y + bbox.height > maxY ) maxY = bbox.y + bbox.height;
            }
        }

        if( typeof this.props.zoomPadding === "number" )
        {
            minX -= this.props.zoomPadding;
            minY -= this.props.zoomPadding;
            maxX += this.props.zoomPadding;
            maxY += this.props.zoomPadding;
        }

        this.zoomToBox( minX, minY, maxX - minX, maxY - minY );
    }

    computeSelectableById(selectable) {
        if (selectable) {
            const selectableById = new Map();
            selectable.forEach((item) => {
                selectableById.set(
                    TriFloorPlanUtils.getPropValue(item, TriSelectablePlugin.SPACE_ID),
                    item
                );
            });
            return selectableById;
        } else {
            return null;
        }
    }

    addSvgTapListener() {
        const svgElement = this.props.triFloorPlan.svgElement;
        if (svgElement !== this.lastSvgElement) {
            if (svgElement != null) {
                svgElement.addEventListener("click", this.svgContainerTapped);
            }
            this.lastSvgElement = svgElement;
        }
    }

    removeSvgTapListener() {
        const svgElement = this.props.triFloorPlan.svgElement;
        if (svgElement != null && svgElement === this.lastSvgElement) {
            svgElement.removeEventListener("click", this.svgContainerTapped);
        }
        this.lastSvgElement = null;
    }

    isValidSelection( targetSpace, recordId )
    {
        if( !recordId ) return false;
        if( !this.selectableById.has( recordId ) ) return false;
        if( targetSpace.isPin ) return true;
        if( !this.props.triFloorPlan.props.spaceLayer ) // Is space attached?
        {
            if( targetSpace.class && targetSpace.class.indexOf( "tri-interactive" ) === -1 ) return false;
        }

        return true;
    }

    svgContainerTapped = debounce(
        (e) => {
            if( this.disableTap )
            {
                this.disableTap = false;
                return;
            }
            const { floorPlanLoaded, enabled, multi, onSelectedChange } = this.props;
            if( !enabled || !this.enabled || !floorPlanLoaded ) return;

            if( !this.selectableById ) return;

            const idproperty = this.props.triFloorPlan.props.spaceLayer
              ? TriSelectablePlugin.EMELEMENT_PROPERTY
              : TriSelectablePlugin.NAME_PROPERTY;
 
            const targetSpace = {
                triRecordId: e.target.getAttribute( idproperty ),
                class: e.target.getAttribute("class"),
                isPin: e.target.getAttribute("pin") === "true",
            };

            let selected = null;
            const recordId = targetSpace.triRecordId;

            if( this.isValidSelection( targetSpace, recordId ) )
            {
                selected = this.selectableById.get(recordId);

                if( multi ) {
                    const selectedMulti = this.selected ? [...this.selected] : [];
                    if (selected && selectedMulti) {
                        const index = selectedMulti.findIndex((item) => {
                          return (
                            TriFloorPlanUtils.getPropValue(
                              selected,
                              TriSelectablePlugin.SPACE_ID
                            ) ===
                            TriFloorPlanUtils.getPropValue(
                              item,
                              TriSelectablePlugin.SPACE_ID
                            )
                          );
                        });
                        if (index !== -1) {
                            selectedMulti.splice(index, 1);
                        } else {
                            selectedMulti.push(selected);
                        }
                        selected = selectedMulti;
                    }
                } else {
                    // If the currently selected item is clicked, unselect it.
                    if(    selected
                        && this.selected
                        && TriFloorPlanUtils.getPropValue( selected, TriSelectablePlugin.SPACE_ID ) ===
                           TriFloorPlanUtils.getPropValue( this.selected, TriSelectablePlugin.SPACE_ID ) )
                    {
                        selected = null;
                    }
                }  
                if (onSelectedChange) onSelectedChange(selected);
            } else {
                if (!multi) {
                  if (onSelectedChange) onSelectedChange(null);
                }
            }
        },
        300,
        { leading: true, trailing: false }
    );

    /*********************************************************************************/
    /* Drag Select
    /*********************************************************************************/

    // Mouse up event
    handleDragSelectEnd( evt )
    {
        const { triFloorPlan } = this.props;
        const { svgElement } = triFloorPlan;
        const container = svgElement.parentElement;

        if( this.dragBox )
        {
            container.removeChild( this.dragBox );
            this.dragBox = null;
        }

        evt.preventDefault();
        evt.stopPropagation();

        let x = this.dragBoundingBox.current.x;
        let y = this.dragBoundingBox.current.y;

        var top, left, height, width;

        left = x > this.dragBoundingBox.start.x ? this.dragBoundingBox.start.x : x;
        width = Math.abs( x - this.dragBoundingBox.start.x );

        top = y > this.dragBoundingBox.start.y ? this.dragBoundingBox.start.y : y;
        height = Math.abs( y - this.dragBoundingBox.start.y );

        this.findSelected( top, left, height, width );

        this.dragBoundingBox = null;
    }

    addDragSelectListeners()
    {
        const { triFloorPlan } = this.props;
        const { svgElement } = triFloorPlan;
        const container = svgElement.parentElement;

        triFloorPlan.pluginInstances.forEach( ( instance ) =>
        {
            if( instance.props.type === TriNavPlugin.type ) instance.enableZoomBox = false;
            if( instance.props.type === TriPanPlugin.type ) instance.enabled = false;
        } );

        container.addEventListener( 'mousedown', this.handleDragStart, true );
        container.addEventListener( 'mousemove', this.handleDragMove, true );
        container.addEventListener( 'mouseup', this.handleDragSelectEnd, true );
    }

    removeDragSelectListeners()
    {
        const { triFloorPlan } = this.props;
        const { svgElement } = triFloorPlan;
        const container = svgElement.parentElement;

        triFloorPlan.pluginInstances.forEach( ( instance ) =>
        {
            if( instance.props.type === TriNavPlugin.type ) instance.enableZoomBox = true;
            if( instance.props.type === TriPanPlugin.type ) instance.enabled = true;
        } );

        container.removeEventListener( 'mousedown', this.handleDragStart, true );
        container.removeEventListener( 'mousemove', this.handleDragMove, true );
        container.removeEventListener( 'mouseup', this.handleDragSelectEnd, true );
    }

    findSelected( top, left, height, width )
    {
        const { triFloorPlan } = this.props;
        const { svgElement, svgViewPort } = triFloorPlan;

        let changed = false;

        let matrix = svgElement.getScreenCTM().inverse();

        const topLeft = svgElement.createSVGPoint();
        topLeft.x = svgViewPort.left + left;
        topLeft.y = svgViewPort.top + top;
        const svgTopLeft = topLeft.matrixTransform( matrix );

        const bottomRight = svgElement.createSVGPoint();
        bottomRight.x = topLeft.x + width;
        bottomRight.y = topLeft.y + height;
        const svgBottomRight = bottomRight.matrixTransform( matrix );

        if( !this.selectableById || this.selectableById.size === 0 ) return;

        const selected = this.props.selected ? [...this.props.selected] : [];

        // Test each selectable space to see if it's bounding box is fully within the
        // selection rectangle.
        this.selectableById.forEach( ( value, key ) =>
        {
            const space = triFloorPlan.attachedMap.get( key );
            if( this.isSpaceInRect( svgTopLeft.x, svgTopLeft.y,
                                    svgBottomRight.x, svgBottomRight.y,
                                    space.getBBox() ) )
            {
                const index = selected.findIndex( ( item ) =>
                {
                    const itemId = TriFloorPlanUtils.getPropValue( item, TriSelectablePlugin.SPACE_ID );
                    return key === itemId;
                } );

                if( index === -1 )
                {
                    changed = true;
                    selected.push( this.selectableById.get( key ) );
                }
            }
        } );

        const { onSelectedChange } = this.props;
        if( typeof onSelectedChange === "function" )
        {
            let newSelection = selected;
            if( changed ) newSelection = [ ...selected ]
            // If drag starts and end in the same space, it can register as
            // a click causing that space to be selected or deselected
            this.disableTap = true;
            setTimeout( async () => onSelectedChange( newSelection ) );
        }
    }

    isSpaceInRect( left, top, right, bottom, bbox )
    {
        if( bbox.x < left ) return false;
        if( bbox.x + bbox.width > right ) return false;
        if( bbox.y < top ) return false;
        if( bbox.y + bbox.height > bottom ) return false;
        return true;
    }
}
